import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Smartphone, Banknote, Copy, Check, ExternalLink, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Holly's manual payment handles
const VENMO_HANDLE = "aviana_yoga";
const VENMO_DEEP_LINK = "https://venmo.com/aviana_yoga";
const ZELLE_EMAIL = "avianayoga@gmail.com";

// Stripe pass-through fee — matches the backend calculation exactly so the
// number shown here matches what the student is charged on Stripe.
const STRIPE_PCT = 0.029;
const STRIPE_FIXED = 0.30;
function calcCardFee(usd) {
  return Math.round((usd * STRIPE_PCT + STRIPE_FIXED) * 100) / 100;
}

// Event-specific payment chooser. Shown AFTER an attendee submits the event
// registration form so Holly always has their info on file even if they
// abandon payment. Three ways to pay: Stripe (fee), Venmo, or Zelle.
export default function PaymentSelectEvent() {
  const { regId } = useParams();
  const navigate = useNavigate();
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/events/registrations/${regId}`);
        setReg(data);
      } catch {
        toast.error("Registration not found.");
        navigate("/");
      }
    })();
  }, [regId, navigate]);

  if (!reg) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  const amount = Number(reg.price_usd) || 0;
  const cardFee = calcCardFee(amount);
  const cardTotal = Math.round((amount + cardFee) * 100) / 100;

  const choose = async (method) => {
    setBusy(true);
    try {
      if (method === "card") {
        const { data } = await api.post(`/events/registrations/${regId}/stripe-checkout`, {
          origin_url: window.location.origin,
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("No checkout URL returned");
      }
      await api.post(`/events/registrations/${regId}/choose-payment`, { method });
      navigate(`/payment-event/${regId}/manual?method=${method}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
    toast.success("Copied");
  };

  const firstName = (reg.full_name || "").split(" ")[0] || "sister";

  return (
    <div className="pt-28 pb-24 bg-aviana-paper" data-testid="payment-event-select-page">
      <div className="container-aviana max-w-3xl">
        <Link to={`/register-event/${reg.offering_slug}`} className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> Back to registration
        </Link>

        {/* Success banner */}
        <div className="bg-aviana-cream border border-aviana-gold/40 rounded-2xl p-6 mb-10 flex items-start gap-4" data-testid="event-reg-confirmation">
          <Sparkles size={20} className="text-aviana-gold mt-1 flex-shrink-0" />
          <div>
            <p className="font-display text-2xl text-aviana-ink leading-tight">Beautiful, {firstName} — your registration is saved.</p>
            <p className="mt-2 text-aviana-inkSoft text-[14px] leading-[1.7]">
              Last step: choose how you'd like to send your <strong className="text-aviana-plum">${amount.toLocaleString()}</strong> for <em>{reg.offering_title}</em>.
            </p>
          </div>
        </div>

        <p className="overline-plum mb-3">Choose your payment method</p>
        <h1 className="font-display text-4xl text-aviana-ink leading-tight">Three ways to pay.</h1>
        <p className="mt-3 text-aviana-muted text-[15px]">
          Card includes a small processing fee. Venmo &amp; Zelle are free for both of us — and your spot is held the same way once Holly confirms funds.
        </p>

        <div className="mt-10 space-y-5">
          {/* Card */}
          <button
            onClick={() => choose("card")}
            disabled={busy}
            className="w-full text-left bg-white border border-aviana-border hover:border-aviana-plum/50 rounded-2xl p-7 transition-all flex items-start gap-5 group"
            data-testid="pay-card-event"
          >
            <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
              <CreditCard size={20} />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <p className="font-display text-2xl text-aviana-ink">Card · Stripe</p>
                <p className="font-display text-2xl text-aviana-plum">${cardTotal.toFixed(2)}</p>
              </div>
              <p className="mt-1 text-aviana-muted text-[13px]">
                ${amount.toLocaleString()} + ${cardFee.toFixed(2)} card processing fee · Secure checkout · Instant confirmation email
              </p>
            </div>
          </button>

          {/* Venmo */}
          <div className="bg-white border border-aviana-border rounded-2xl p-7" data-testid="pay-venmo-event">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
                <Smartphone size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <p className="font-display text-2xl text-aviana-ink">Venmo</p>
                  <p className="font-display text-2xl text-aviana-ink">${amount.toLocaleString()}</p>
                </div>
                <p className="mt-1 text-aviana-muted text-[13px]">
                  No processing fee. Send to <strong className="text-aviana-plum">@{VENMO_HANDLE}</strong> with your full name and the event name in the note.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href={VENMO_DEEP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5"
                    data-testid="open-venmo-event"
                  >
                    Open Venmo <ExternalLink size={11} />
                  </a>
                  <button onClick={() => copy(VENMO_HANDLE, "venmo")} className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5">
                    {copied === "venmo" ? <Check size={11} /> : <Copy size={11} />}
                    {copied === "venmo" ? "Copied" : `@${VENMO_HANDLE}`}
                  </button>
                  <button
                    onClick={() => choose("venmo")}
                    disabled={busy}
                    className="btn-primary !py-2 !px-5 !text-[11px]"
                    data-testid="confirm-venmo-event"
                  >
                    I'll pay via Venmo →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Zelle */}
          <div className="bg-white border border-aviana-border rounded-2xl p-7" data-testid="pay-zelle-event">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
                <Banknote size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <p className="font-display text-2xl text-aviana-ink">Zelle</p>
                  <p className="font-display text-2xl text-aviana-ink">${amount.toLocaleString()}</p>
                </div>
                <p className="mt-1 text-aviana-muted text-[13px]">
                  No processing fee. Send to <strong className="text-aviana-plum">{ZELLE_EMAIL}</strong> through your banking app. Recipient may appear as <em>Ananda Kamala Academy</em>.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => copy(ZELLE_EMAIL, "zelle")} className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5">
                    {copied === "zelle" ? <Check size={11} /> : <Copy size={11} />}
                    {copied === "zelle" ? "Copied" : ZELLE_EMAIL}
                  </button>
                  <button
                    onClick={() => choose("zelle")}
                    disabled={busy}
                    className="btn-primary !py-2 !px-5 !text-[11px]"
                    data-testid="confirm-zelle-event"
                  >
                    I'll pay via Zelle →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-[12px] text-aviana-muted mt-10">
          Questions? Email Holly at <a href="mailto:holly@avianayoga.com" className="text-aviana-plum underline underline-offset-2">holly@avianayoga.com</a>.
        </p>
      </div>
    </div>
  );
}
